// src/analytics/GtagAnalytics.js
import { getUtmFromSession } from "./urlUtmParamsHelpers";

let queue = [];
let context = {};
let defaults = { app: "web" };
let inited = false;
let debug = true;

let includeSessionUtmParamsDefault = true;

const timers = new Map();
const MAX_QUEUE = 500;

// flush retry config
const FLUSH_RETRY_MS = 500;
const FLUSH_RETRY_MAX_MS = 15000;
let flushRetryTimer = null;
let flushRetryStartedAt = 0;

function log(...args) {
  if (!debug || typeof console === "undefined") return;
  // eslint-disable-next-line no-console
  console.log("[GA4]", ...args);
}

function hasGtag() {
  return typeof window !== "undefined" && typeof window.gtag === "function";
}

function hasDataLayer() {
  return typeof window !== "undefined" && Array.isArray(window.dataLayer);
}

function available() {
  const ok = typeof window !== "undefined" && (hasGtag() || hasDataLayer());
  log("available()", ok);
  return ok;
}

function normalizeName(name) {
  return String(name || "")
    .trim()
    .replace(/\s+/g, "_")
    .toLowerCase();
}

function toNonEmptyString(v) {
  const s = String(v == null ? "" : v).trim();
  return s.length > 0 ? s : undefined;
}

function pickUtmFromSession() {
  const utm =
    getUtmFromSession && typeof getUtmFromSession === "function"
      ? getUtmFromSession()
      : null;

  if (!utm || typeof utm !== "object") {
    return null;
  }

  const picked = {
    utm_source: toNonEmptyString(utm.utm_source),
    utm_medium: toNonEmptyString(utm.utm_medium),
    utm_campaign: toNonEmptyString(utm.utm_campaign),
    utm_content: toNonEmptyString(utm.utm_content),
    utm_id: toNonEmptyString(utm.utm_id),
  };

  const hasAny =
    !!picked.utm_source ||
    !!picked.utm_medium ||
    !!picked.utm_campaign ||
    !!picked.utm_content ||
    !!picked.utm_id;

  return hasAny ? picked : null;
}

function pickUtmOverride(params) {
  const u = params && typeof params === "object" ? params.utm : null;
  if (!u || typeof u !== "object") {
    return null;
  }

  const picked = {
    utm_source: toNonEmptyString(u.utm_source),
    utm_medium: toNonEmptyString(u.utm_medium),
    utm_campaign: toNonEmptyString(u.utm_campaign),
    utm_content: toNonEmptyString(u.utm_content),
    utm_id: toNonEmptyString(u.utm_id),
  };

  const hasAny =
    !!picked.utm_source ||
    !!picked.utm_medium ||
    !!picked.utm_campaign ||
    !!picked.utm_content ||
    !!picked.utm_id;

  return hasAny ? picked : null;
}

function stripInternalFields(params) {
  if (!params || typeof params !== "object") {
    return params;
  }

  const out = { ...params };
  delete out.utm;
  delete out.includeSessionUtmParams;
  return out;
}

function mergeAndClean(obj) {
  const out = { ...obj };
  Object.keys(out).forEach((k) => {
    if (out[k] === undefined) {
      delete out[k];
    }
  });
  return out;
}

function buildPayload(params) {
  const rawParams = params || {};
  const includeSession =
    rawParams.includeSessionUtmParams !== undefined
      ? Boolean(rawParams.includeSessionUtmParams)
      : includeSessionUtmParamsDefault;

  const utmOverride = pickUtmOverride(rawParams);
  const utmSession = includeSession ? pickUtmFromSession() : null;

  // precedence: explicit override > session
  const utm = utmOverride || utmSession || null;

  const cleanedParams = stripInternalFields(rawParams);

  return mergeAndClean({
    ...defaults,
    ...context,
    ...(utm || {}),
    ...cleanedParams,
  });
}

function _enqueue(evt, payload) {
  if (queue.length >= MAX_QUEUE) {
    queue.shift();
    log("queue full, dropping oldest event");
  }
  queue.push([evt, payload]);
}

function _sendNow(evt, payload) {
  if (hasGtag()) {
    log("send via gtag()", evt, payload);
    window.gtag("event", evt, payload);
    return true;
  }

  if (hasDataLayer()) {
    log("send via dataLayer.push()", evt, payload);
    window.dataLayer.push({ event: evt, ...payload });
    return true;
  }

  return false;
}

function _send(name, params) {
  const evt = normalizeName(name);
  const payload = buildPayload(params);

  const sent = _sendNow(evt, payload);
  if (sent) {
    return true;
  }

  log("queue event (gtag not ready)", evt, payload);
  _enqueue(evt, payload);
  return false;
}

function _flushOnce() {
  if (!available()) {
    log("flush skipped, gtag not available");
    return false;
  }

  if (queue.length === 0) {
    log("flush: nothing to flush");
    return true;
  }

  log("flushing queue", queue.length, "events");
  const pending = queue;
  queue = [];

  for (const [evt, payload] of pending) {
    const sent = _sendNow(evt, payload);
    if (!sent) {
      log("flush failed mid-way, re-queueing remaining events");
      _enqueue(evt, payload);
      // re-queue the rest (preserve order)
      for (
        let i = pending.indexOf([evt, payload]) + 1;
        i < pending.length;
        i++
      ) {
        const [e2, p2] = pending[i];
        _enqueue(e2, p2);
      }
      return false;
    }
  }

  return true;
}

function _stopFlushRetry() {
  if (flushRetryTimer != null) {
    clearInterval(flushRetryTimer);
    flushRetryTimer = null;
  }
  flushRetryStartedAt = 0;
}

function _startFlushRetry() {
  if (flushRetryTimer != null) return;

  flushRetryStartedAt = Date.now();
  flushRetryTimer = setInterval(() => {
    const elapsed = Date.now() - flushRetryStartedAt;
    if (elapsed >= FLUSH_RETRY_MAX_MS) {
      log("flush retry stopped (max time reached)");
      _stopFlushRetry();
      return;
    }

    const ok = _flushOnce();
    if (ok) {
      log("flush retry succeeded, stopping");
      _stopFlushRetry();
    }
  }, FLUSH_RETRY_MS);
}

const GtagAnalytics = {
  init(opts = {}) {
    if (inited) {
      log("init skipped (already initialized)");
      return;
    }

    inited = true;
    defaults = { ...defaults, ...opts.defaults };
    debug = Boolean(opts.debug);

    if (opts.includeSessionUtmParams !== undefined) {
      includeSessionUtmParamsDefault = Boolean(opts.includeSessionUtmParams);
    }

    log("init()", {
      debug,
      defaults,
      includeSessionUtmParamsDefault,
    });

    // Attempt immediate flush; if not available, retry for a while.
    const ok = _flushOnce();
    if (!ok) {
      _startFlushRetry();
    }

    if (typeof window !== "undefined") {
      window.addEventListener("error", (e) => {
        log("window error captured", e.message);
        GtagAnalytics.track("js_error", {
          message: String(e.message),
          file: e.filename,
          line: e.lineno,
          col: e.colno,
        });
      });

      window.addEventListener("unhandledrejection", (e) => {
        log("unhandled promise rejection", e.reason);
        GtagAnalytics.track("promise_rejection", {
          reason: String(e.reason),
        });
      });
    }
  },

  setContext(obj) {
    context = { ...context, ...obj };
    log("setContext()", context);
  },

  // Returns true if sent immediately, false if queued
  track(name, params = {}) {
    log("track()", name, params);

    // Try flushing queued events first; if still not possible, keep retry loop running.
    const flushed = _flushOnce();
    if (!flushed) {
      _startFlushRetry();
    }

    const sent = _send(name, params);

    // If it was queued, ensure retry loop is running so it can flush later.
    if (!sent) {
      _startFlushRetry();
    }

    return sent;
  },

  error(eventName = "app_error", err, extra = {}) {
    const base =
      err && typeof err === "object"
        ? { name: err.name, message: String(err.message) }
        : { message: String(err) };

    log("error()", eventName, base, extra);
    return GtagAnalytics.track(eventName, { ...base, ...extra });
  },

  timeStart(key) {
    if (typeof performance === "undefined") return;
    timers.set(key, performance.now());
    log("timeStart()", key);
  },

  timeEnd(key, eventName, params = {}) {
    if (typeof performance === "undefined") return;

    const t0 = timers.get(key);
    if (t0 == null) {
      log("timeEnd() called without timeStart()", key);
      return;
    }

    timers.delete(key);
    const duration = Math.round(performance.now() - t0);

    log("timeEnd()", key, duration, "ms");
    GtagAnalytics.track(eventName, {
      duration_ms: duration,
      ...params,
    });
  },

  handler(prefix = "") {
    const p = prefix ? String(prefix) : "";
    return (name, detail = {}) => {
      const evt = p ? `${p}_${name}` : name;
      log("handler()", evt, detail);
      GtagAnalytics.track(evt, detail);
    };
  },

  // Optional helpers you can use from pages to decide whether to markSent:
  isAvailable() {
    return available();
  },

  // Force a flush attempt (and start retry loop if needed)
  flush() {
    const ok = _flushOnce();
    if (!ok) {
      _startFlushRetry();
    }
    return ok;
  },
};

export default GtagAnalytics;
